import { Smile } from "lucide-react";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { EmojiPickerPanel } from "@/components/common/EmojiPickerPanel";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { loadRecentEmojis, saveRecentEmoji } from "@/lib/emoji/recentEmoji";

interface EmojiPickerButtonProps {
  onSelect: (emoji: string) => void;
  onBeforeOpen?: () => void;
  onOpenChange?: (open: boolean) => void;
}

export const EmojiPickerButton: React.FC<EmojiPickerButtonProps> = ({
  onSelect,
  onBeforeOpen,
  onOpenChange,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [recentEmojis, setRecentEmojis] = useState<string[]>([]);
  const pointerSavedSelectionRef = useRef(false);

  useEffect(() => {
    setRecentEmojis(loadRecentEmojis());
  }, []);

  const handleSelect = (emoji: string) => {
    onSelect(emoji);
    setRecentEmojis(saveRecentEmoji(emoji));
    setOpen(false);
    onOpenChange?.(false);
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    onOpenChange?.(newOpen);
  };

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="formattingInactive"
            size="formatting"
            className="size-8 p-0 text-sm leading-none"
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
              pointerSavedSelectionRef.current = true;
              onBeforeOpen?.();
            }}
            onClick={() => {
              if (!pointerSavedSelectionRef.current) {
                onBeforeOpen?.();
              }
              pointerSavedSelectionRef.current = false;
              handleOpenChange(true);
            }}
            aria-label={t("common.emoji")}
          >
            <Smile className="size-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{t("common.emoji")}</TooltipContent>
      </Tooltip>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="w-[min(92vw,420px)]">
          <DialogHeader>
            <DialogTitle>{t("common.emoji")}</DialogTitle>
            <DialogDescription className="sr-only">
              {t("common.selectEmoji")}
            </DialogDescription>
          </DialogHeader>
          <EmojiPickerPanel
            onSelect={handleSelect}
            recentEmojis={recentEmojis}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};
